// The pieces "Transacciones y reversas" and "Cierre de conciliación" are drawn with, so both read the
// same way: a figure card that never lets its number run out of it and says a large one in words, a
// card around a chart or a table, a table that scrolls inside its card instead of overflowing the page,
// a chart that says "nothing in this period" instead of drawing empty axes, and bars of a share.
//
// Every text has its colour set: the dark card's white is written out, never inherited, because the
// shell's theme colours text inside `.brinks-scope` and a dark card read grey on black.
import { useEffect, useState } from "react";
import { cn } from "@mantis-sas/ui";

import { montoLegible } from "../../../utils/cierrePanel";
import { cifraEnPalabras, tamanoDeCifra } from "../../../utils/cierreCifras";
import { guiaDe } from "../../../utils/cierreTours";

/** A figure: its label, the number (sized to fit), the number in words when large, and a detail line. */
export function Cifra({
  icono: Icono, etiqueta, valor, palabras, detalle, destacada = false, tono = null,
}) {
  return (
    <article
      {...guiaDe(etiqueta, detalle)}
      className={cn(
        "flex min-w-0 flex-col rounded-[14px] border p-4 shadow-brinks-card",
        destacada ? "border-transparent bg-primary" : "bg-card"
      )}
    >
      <div className="flex items-center gap-2">
        <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-[9px]", destacada ? "bg-white/10 text-white" : tono || "bg-brinks-surface text-brinks-foreground")}>
          <Icono className="h-4 w-4" />
        </span>
        <p className={cn("min-w-0 text-[12px] font-medium leading-tight", destacada ? "text-white/75" : "text-brinks-muted-foreground")}>{etiqueta}</p>
      </div>
      <p
        className={cn(
          "mt-3 break-words font-semibold leading-tight tabular-nums",
          tamanoDeCifra(valor),
          destacada ? "text-white" : "text-brinks-foreground"
        )}
        title={typeof valor === "string" ? valor : undefined}
      >
        {valor}
      </p>
      {palabras ? (
        <p className={cn("mt-0.5 text-[11px] leading-snug", destacada ? "text-white/60" : "text-brinks-muted-foreground")}>{palabras}</p>
      ) : null}
      {detalle ? (
        <p className={cn("mt-2 text-[12px] leading-snug", destacada ? "text-white/80" : "text-brinks-muted-foreground")}>{detalle}</p>
      ) : null}
    </article>
  );
}

/** "≈ 1,56 billones de pesos" under a money figure; nothing for a small one. */
export const enPalabras = (valor, opciones) => {
  const texto = cifraEnPalabras(valor, opciones);
  return texto ? `≈ ${texto}` : null;
};

/**
 * B-174 — whether the screen is a phone's (under `sm`, 640 px): a chart then drops its second axis and
 * keeps its plot wide. Follows the screen as it turns or resizes.
 */
export function useEstrecho() {
  const consulta = "(max-width: 639px)";
  const [estrecho, setEstrecho] = useState(() => typeof window !== "undefined" && Boolean(window.matchMedia) && window.matchMedia(consulta).matches);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const media = window.matchMedia(consulta);
    const alCambiar = () => setEstrecho(media.matches);
    if (media.addEventListener) media.addEventListener("change", alCambiar);
    return () => { if (media.removeEventListener) media.removeEventListener("change", alCambiar); };
  }, []);
  return estrecho;
}

/**
 * A card around a chart or a table: title, subtitle, an optional control on the right. The title keeps
 * at least 14rem: on a phone the control goes below it rather than squeezing it to a word per line.
 */
export function Tarjeta({
  titulo, subtitulo, extra, children, className,
}) {
  return (
    <section {...guiaDe(titulo, subtitulo)} className={cn("flex min-w-0 flex-col overflow-hidden rounded-[14px] border bg-card shadow-brinks-card", className)} aria-label={titulo}>
      <header className="flex flex-wrap items-start justify-between gap-2 border-b px-4 py-3">
        <div className="min-w-[min(100%,14rem)] flex-1">
          <h3 className="text-[14px] font-semibold text-brinks-foreground">{titulo}</h3>
          {subtitulo ? <p className="mt-0.5 text-[12px] leading-snug text-brinks-muted-foreground">{subtitulo}</p> : null}
        </div>
        {extra ? <div className="shrink-0">{extra}</div> : null}
      </header>
      <div className="min-w-0 p-3">{children}</div>
    </section>
  );
}

/**
 * A table that scrolls inside its card, both ways, with its heading fixed: `columnas` are
 * `{ titulo, numerica? }`, a numeric column right-aligned and never broken. Rows are the caller's
 * `<tr>`s, drawn with `celda()`.
 */
export function Tabla({
  columnas, children, vacia, etiqueta, alto = "max-h-[360px]",
}) {
  return (
    <div className={cn("overflow-auto rounded-[10px] border", alto)}>
      <table className="w-full min-w-max border-collapse text-[12.5px]" aria-label={etiqueta}>
        <thead className="sticky top-0 z-[1] bg-brinks-surface">
          <tr className="border-b">
            {columnas.map((c) => (
              <th
                key={c.titulo}
                scope="col"
                className={cn(
                  "whitespace-nowrap px-3 py-2 text-[11.5px] font-semibold text-brinks-muted-foreground",
                  c.numerica ? "text-right" : "text-left"
                )}
              >
                {c.titulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
      {vacia ? <p className="px-3 py-6 text-center text-[12.5px] text-brinks-muted-foreground">{vacia}</p> : null}
    </div>
  );
}

/** A cell of `Tabla`: numbers right-aligned and on one line, the first column a little stronger. */
export const celda = ({ numerica = false, principal = false, tono } = {}) => cn(
  "px-3 py-2 align-top",
  numerica ? "whitespace-nowrap text-right tabular-nums" : "min-w-[120px]",
  principal ? "font-medium text-brinks-foreground" : "text-brinks-foreground/90",
  tono
);

/**
 * A row of `Tabla`, shaded on hover; a row that opens something says so with its cursor. The line under
 * it is the row's, not its cells': a stylesheet of the app takes the border off every row's last cell.
 */
export const fila = (abre = false) => cn("border-b last:border-b-0 hover:bg-brinks-surface/70", abre && "cursor-pointer");

/** A chart legend's words in the text colour, never in their series' (a pale series' name was unreadable). */
export const leyendaLegible = (valor) => <span style={{ color: "var(--brinks-foreground)" }}>{valor}</span>;

/** A chart's place: the chart, or a sentence when the period has nothing to draw. */
export function LugarDeGrafica({ vacio, alto = "h-[280px]", children }) {
  if (vacio) {
    return (
      <div className={cn("grid place-items-center rounded-[10px] border border-dashed text-[12.5px] text-brinks-muted-foreground", alto)}>
        {vacio}
      </div>
    );
  }
  return <div className={cn("min-w-0", alto)}>{children}</div>;
}

/** A list of `{ key, label, count, value }` as bars of their share: motives, attributions, types. */
export function Barras({ filas, vacio = "Sin reversas en el periodo" }) {
  const total = filas.reduce((suma, f) => suma + f.count, 0) || 1;
  if (!filas.length) return <p className="py-6 text-center text-[12.5px] text-brinks-muted-foreground">{vacio}</p>;
  return (
    <ul className="space-y-3">
      {filas.map((f) => {
        const parte = Math.round((f.count / total) * 100);
        return (
          <li key={f.key} className="min-w-0">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 break-words text-[12.5px] font-medium text-brinks-foreground">{f.label}</span>
              <span className="shrink-0 text-[12px] tabular-nums text-brinks-muted-foreground">{parte}%</span>
            </div>
            <div className="mt-1 h-2 rounded-full bg-brinks-surface">
              <div className="h-2 rounded-full bg-primary" style={{ width: `${Math.max(2, (f.count / total) * 100)}%` }} />
            </div>
            <p className="mt-0.5 text-[11.5px] tabular-nums text-brinks-muted-foreground">
              {f.count} {f.count === 1 ? "reversa" : "reversas"} · {montoLegible(f.value)}
            </p>
          </li>
        );
      })}
    </ul>
  );
}

/** The chips of a period: one row that wraps, the chosen one filled. */
export function Chips({ opciones, elegida, onElegir, etiqueta }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label={etiqueta}>
      {opciones.map((o) => (
        <button
          key={o.clave}
          type="button"
          onClick={() => onElegir(o.clave)}
          aria-pressed={elegida === o.clave}
          className={cn(
            "h-8 whitespace-nowrap rounded-full border px-3 text-[12.5px] font-medium transition",
            elegida === o.clave ? "border-transparent bg-primary text-white" : "bg-card text-brinks-foreground hover:bg-brinks-surface"
          )}
        >
          {o.etiqueta}
        </button>
      ))}
    </div>
  );
}

/** A select of the filter row, the same height and shape as the chips. */
export const SELECT = "h-8 max-w-full rounded-full border bg-card px-3 text-[12.5px] text-brinks-foreground";

/**
 * A segmented control: the options of one choice together in one capsule, the chosen one filled — the
 * period of a report. It wraps inside its capsule on a narrow screen instead of running out of it.
 */
export function Segmentado({ opciones, elegida, onElegir, etiqueta }) {
  return (
    <div className="inline-flex max-w-full flex-wrap gap-0.5 rounded-[12px] border bg-brinks-surface p-1" role="group" aria-label={etiqueta}>
      {opciones.map((o) => (
        <button
          key={o.clave}
          type="button"
          onClick={() => onElegir(o.clave)}
          aria-pressed={elegida === o.clave}
          className={cn(
            "h-8 whitespace-nowrap rounded-[9px] px-3 text-[12.5px] font-medium transition",
            elegida === o.clave ? "bg-primary text-white shadow-sm" : "text-brinks-foreground hover:bg-card"
          )}
        >
          {o.etiqueta}
        </button>
      ))}
    </div>
  );
}

/** One filter of a `BarraDeFiltros`: its label above it, the control filling its column. */
export function Filtro({ etiqueta, children, className }) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-1", className)}>
      <span className="text-[11.5px] font-medium text-brinks-muted-foreground">{etiqueta}</span>
      {children}
    </div>
  );
}

/** A select or a date that fills its column of the filter bar. */
export const SELECT_DE_FILTRO = "h-10 w-full min-w-0 rounded-[10px] border bg-card px-3 text-[12.5px] text-brinks-foreground";

/**
 * The filter bar of a report, across the whole width: the period on the left, the other filters on the
 * right in equal columns — never a pile of controls on one side.
 */
export function BarraDeFiltros({ periodo, children }) {
  return (
    <div className="grid gap-3 rounded-[14px] border bg-card p-3 shadow-brinks-card lg:grid-cols-[auto_minmax(0,1fr)] lg:items-end">
      <div className="min-w-0">{periodo}</div>
      {children ? <div className="min-w-0">{children}</div> : null}
    </div>
  );
}
